import { registerSlider } from '@theme/lib-swiper-manager';

const DEFAULT_TRANSITION_DURATION = 800;
const INTERACTION_RESUME_DELAY = 1200;

/**
 * @param {Element} swiperEl
 * @param {HTMLElement} sectionEl
 * @returns {object}
 */
registerSlider('[data-collection-banner]', function (swiperEl, sectionEl) {
  const slides = Array.from(
    sectionEl.querySelectorAll('.collection-banner__slide')
  );
  const indicators = Array.from(
    sectionEl.querySelectorAll('.collection-banner__indicator')
  );
  const progressCircles = Array.from(
    sectionEl.querySelectorAll('.collection-banner__indicator-progress-circle')
  ).filter(function (el) {
    return el instanceof SVGElement;
  });

  const duration = parseInt(sectionEl.dataset.duration || '5000', 10);
  const shouldAutoplay =
    (sectionEl.dataset.autoplay || 'false') === 'true' &&
    !window.Shopify?.designMode;
  const pauseOnHover = (sectionEl.dataset.pauseOnHover || 'true') !== 'false';
  const pauseOnFocus = (sectionEl.dataset.pauseOnFocus || 'true') !== 'false';
  const hasMultipleSlides = slides.length > 1;

  let currentIndex = 0;
  let activeProgressCircle = progressCircles[0] || null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let interactionResumeTimeout = null;
  /** @type {any} */
  let swiper = null;
  let circumference = 282.74;

  /**
   * Calculate circumference from indicator size CSS var
   * @returns {number}
   */
  function calculateCircumference() {
    if (indicators.length === 0) return 282.74;
    const indicator = indicators[0];
    if (!(indicator instanceof HTMLElement)) return 282.74;
    const computedStyle = getComputedStyle(indicator);
    const size = parseFloat(computedStyle.getPropertyValue('--indicator-size'));
    if (size && size > 0) {
      const radius = 45;
      return 2 * Math.PI * radius;
    }
    return 282.74;
  }

  circumference = calculateCircumference();

  function canControlAutoplay() {
    return Boolean(
      swiper && swiper.autoplay && shouldAutoplay && hasMultipleSlides
    );
  }

  function pauseAutoplay() {
    if (!canControlAutoplay()) return;
    if (swiper.autoplay.running && !swiper.autoplay.paused) {
      swiper.autoplay.pause();
    }
  }

  function resumeAutoplay() {
    if (!canControlAutoplay()) return;

    if (pauseOnHover && sectionEl.matches(':hover')) return;
    if (pauseOnFocus && sectionEl.matches(':focus-within')) return;

    if (
      swiper.autoplay.paused &&
      typeof swiper.autoplay.resume === 'function'
    ) {
      swiper.autoplay.resume();
      return;
    }

    if (
      !swiper.autoplay.running &&
      typeof swiper.autoplay.start === 'function'
    ) {
      swiper.autoplay.start();
    }
  }

  /**
   * @param {SVGElement} circle
   * @param {number} progress
   */
  function applyProgressToCircle(circle, progress) {
    if (!(circle instanceof SVGElement)) return;
    const offset = circumference - (circumference * progress) / 100;
    circle.style.transition = 'none';
    circle.style.strokeDasharray = String(circumference);
    circle.style.strokeDashoffset = String(offset);
  }

  function initializeProgressCircles() {
    progressCircles.forEach(function (circle) {
      applyProgressToCircle(circle, 0);
    });
  }

  /**
   * @param {number} progress
   */
  function updateIndicatorProgress(progress) {
    if (!activeProgressCircle) return;
    applyProgressToCircle(activeProgressCircle, progress);
  }

  function updateIndicatorAria() {
    indicators.forEach(function (indicator, index) {
      indicator.setAttribute(
        'aria-current',
        index === currentIndex ? 'true' : 'false'
      );
    });
  }

  function updateSlideAria() {
    Array.from(sectionEl.querySelectorAll('.collection-banner__slide')).forEach(
      function (slide, index) {
        const isActive =
          slide.classList.contains('swiper-slide-active') ||
          (index === currentIndex &&
            !slide.classList.contains('swiper-slide-duplicate'));

        slide.toggleAttribute('inert', !isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      }
    );
  }

  /**
   * @param {number} index
   */
  function updateIndicators(index) {
    indicators.forEach(function (indicator, i) {
      indicator.classList.toggle('is-active', i === index);
    });
    activeProgressCircle = progressCircles[index] || null;
    updateIndicatorProgress(0);
  }

  function clearInteractionResume() {
    if (interactionResumeTimeout) {
      clearTimeout(interactionResumeTimeout);
      interactionResumeTimeout = null;
    }
  }

  function scheduleInteractionResume() {
    if (!canControlAutoplay()) return;
    clearInteractionResume();
    interactionResumeTimeout = setTimeout(function () {
      resumeAutoplay();
    }, INTERACTION_RESUME_DELAY);
  }

  /**
   * @param {Event} event
   */
  var handleKeydown = function (event) {
    if (!swiper) return;
    if (!(event instanceof KeyboardEvent)) return;
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        swiper.slidePrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        swiper.slideNext();
        break;
      case 'Home':
        event.preventDefault();
        swiper.slideToLoop(0);
        break;
      case 'End':
        event.preventDefault();
        swiper.slideToLoop(slides.length - 1);
        break;
    }
  };

  var handleMouseEnter = function () {
    pauseAutoplay();
  };

  var handleMouseLeave = function () {
    scheduleInteractionResume();
  };

  var handleFocusIn = function () {
    clearInteractionResume();
    pauseAutoplay();
  };

  /**
   * @param {Event} event
   */
  var handleFocusOut = function (event) {
    const focusEvent = /** @type {FocusEvent} */ (event);
    if (
      focusEvent.relatedTarget &&
      focusEvent.relatedTarget instanceof Node &&
      sectionEl.contains(focusEvent.relatedTarget)
    ) {
      return;
    }

    scheduleInteractionResume();
  };

  var indicatorListeners = indicators.map(function (indicator, index) {
    var listener = function () {
      if (swiper) swiper.slideToLoop(index);
    };
    indicator.addEventListener('click', listener);
    return { indicator: indicator, listener: listener };
  });

  sectionEl.addEventListener('keydown', handleKeydown);

  if (pauseOnHover) {
    sectionEl.addEventListener('mouseenter', handleMouseEnter);
    sectionEl.addEventListener('mouseleave', handleMouseLeave);
  }

  if (pauseOnFocus) {
    sectionEl.addEventListener('focusin', handleFocusIn);
    sectionEl.addEventListener('focusout', handleFocusOut);
  }

  return {
    effect: 'fade',
    fadeEffect: { crossFade: true },
    loop: hasMultipleSlides,
    speed: DEFAULT_TRANSITION_DURATION,
    autoplay:
      shouldAutoplay && hasMultipleSlides
        ? {
            delay: duration,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }
        : false,
    allowTouchMove: hasMultipleSlides,
    grabCursor: hasMultipleSlides,
    a11y: { enabled: false },
    on: {
      /**
       * @param {any} sw
       */
      init: function (sw) {
        swiper = sw;
        initializeProgressCircles();
        updateIndicators(0);
        updateSlideAria();
      },
      /**
       * @param {any} _sw
       * @param {number} _timeLeft
       * @param {number} percentage
       */
      autoplayTimeLeft: function (_sw, _timeLeft, percentage) {
        updateIndicatorProgress((1 - percentage) * 100);
      },
      /**
       * @param {any} sw
       */
      slideChange: function (sw) {
        currentIndex = sw.realIndex;
        updateIndicatorAria();
        updateSlideAria();
        updateIndicators(sw.realIndex);
      },
      destroy: function () {
        sectionEl.removeEventListener('keydown', handleKeydown);
        sectionEl.removeEventListener('mouseenter', handleMouseEnter);
        sectionEl.removeEventListener('mouseleave', handleMouseLeave);
        sectionEl.removeEventListener('focusin', handleFocusIn);
        sectionEl.removeEventListener('focusout', handleFocusOut);
        indicatorListeners.forEach(function (item) {
          item.indicator.removeEventListener('click', item.listener);
        });
        clearInteractionResume();
        swiper = null;
      },
    },
  };
});
